import { logger } from '@adornis/base/logging.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TaskType } from '../../../../../../db/api/task-types/task-main.js';
import type { StructureEditorProps } from '../structure-editor.js';
import type { PageBoxItem, SelectionAnchor, StructureEditorAdditionalInfo, StructureEditorTask } from '../utils.js';
import {
  aggregateBoxText,
  FALLBACK_PAGE_ASPECT_RATIO,
  flattenAdditionalInfo,
  flattenTasks,
  generateUuid,
  getNextMainTaskNumber,
  getSelectionAnchor,
  mergeAdditionalInfoPreservingLocalGeometry,
  mergeTasksPreservingLocalGeometry,
  normalizeBox,
  reconcileMatchedTaskFrames,
  resolveSelectionAnchor,
  uniqueStrings,
} from '../utils.js';

export function useStructureState(props: StructureEditorProps) {
  const { exam, isReadOnly = false, onSave, onSaveAdditionalInfo } = props;

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedBoxKey, setSelectedBoxKey] = useState<string | null>(null);
  const [selectedAdditionalInfoId, setSelectedAdditionalInfoId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<StructureEditorTask[]>([]);
  const [localAdditionalInfo, setLocalAdditionalInfo] = useState<StructureEditorAdditionalInfo[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [cardHeightsPx, setCardHeightsPx] = useState<Record<string, number>>({});
  const [pageAspectRatios, setPageAspectRatios] = useState<Record<number, number>>({});
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const imageContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tasksRef = useRef<StructureEditorTask[]>([]);
  const localAdditionalInfoRef = useRef<StructureEditorAdditionalInfo[]>([]);
  const selectedBoxKeyRef = useRef<string | null>(null);
  const selectedAnchorRef = useRef<SelectionAnchor | null>(null);
  const pendingSaveRef = useRef<{
    tasks: StructureEditorTask[];
    options?: { rerunDescriptionOCRBoxIds?: string[] };
  } | null>(null);
  const saveInFlightRef = useRef(false);
  const pendingAdditionalInfoSaveRef = useRef<StructureEditorAdditionalInfo[] | null>(null);
  const additionalInfoSaveInFlightRef = useRef(false);
  const hasPendingSelectionChangesRef = useRef(false);
  const cardRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const onSaveRef = useRef(onSave);
  const isReadOnlyRef = useRef(isReadOnly);

  useEffect(() => {
    onSaveRef.current = onSave;
  });
  useEffect(() => {
    isReadOnlyRef.current = isReadOnly;
  });

  useEffect(() => {
    if (saveInFlightRef.current) return;

    const serverTasks = flattenTasks(exam);
    // Auto-convert matched task frames to dynamic on load (drop a redundant declared total == children sum),
    // so editing their children later never resurfaces a mismatch.
    const mergedTasks = reconcileMatchedTaskFrames(mergeTasksPreservingLocalGeometry(tasksRef.current, serverTasks));

    setTasks(mergedTasks);
    tasksRef.current = mergedTasks;

    const currentSelection = selectedBoxKeyRef.current;
    const isAdditionalInfoSelection = currentSelection?.startsWith('info::') ?? false;

    if (currentSelection && !isAdditionalInfoSelection) {
      const selectedEntry = mergedTasks
        .flatMap(task => task.boundingBoxes.map(box => ({ taskId: task.id, boxKey: `${task.id}-${box.id}` })))
        .find(entry => entry.boxKey === currentSelection);

      if (selectedEntry) {
        setSelectedTaskId(selectedEntry.taskId);
        setSelectedBoxKey(selectedEntry.boxKey);
        selectedBoxKeyRef.current = selectedEntry.boxKey;
        selectedAnchorRef.current = getSelectionAnchor(mergedTasks, selectedEntry.taskId, selectedEntry.boxKey);
        return;
      }
    }

    // An additionalInfo box is selected — the additionalInfo useEffect handles its own sync,
    // so don't touch the selection here
    if (isAdditionalInfoSelection) return;

    const anchorSelection = resolveSelectionAnchor(mergedTasks, selectedAnchorRef.current);
    if (anchorSelection) {
      setSelectedTaskId(anchorSelection.taskId);
      setSelectedBoxKey(anchorSelection.boxKey);
      selectedBoxKeyRef.current = anchorSelection.boxKey;
      return;
    }

    setSelectedTaskId(null);
    setSelectedBoxKey(null);
    selectedBoxKeyRef.current = null;
    selectedAnchorRef.current = null;
  }, [exam]);

  useEffect(() => {
    if (additionalInfoSaveInFlightRef.current) return;
    const serverInfo = flattenAdditionalInfo(exam);
    const mergedInfo = mergeAdditionalInfoPreservingLocalGeometry(localAdditionalInfoRef.current, serverInfo);
    setLocalAdditionalInfo(mergedInfo);
    localAdditionalInfoRef.current = mergedInfo;
  }, [exam]);

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  useEffect(() => {
    localAdditionalInfoRef.current = localAdditionalInfo;
  }, [localAdditionalInfo]);

  useEffect(() => {
    selectedBoxKeyRef.current = selectedBoxKey;
  }, [selectedBoxKey]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(
    () => () => {
      if (isReadOnlyRef.current || !hasPendingSelectionChangesRef.current) return;
      if (tasksRef.current.length === 0) return;
      void onSaveRef.current(tasksRef.current).catch(() => {});
    },
    [],
  );

  const selectedTask = tasks.find(task => task.id === selectedTaskId) ?? null;

  const taskTypes = (Object.values(TaskType) as TaskType[]).filter(taskType => taskType !== TaskType.DEFAULT);

  const flushPersistQueue = () => {
    if (saveInFlightRef.current) return;
    const nextSave = pendingSaveRef.current;
    if (!nextSave) {
      setIsSaving(false);
      return;
    }

    pendingSaveRef.current = null;
    saveInFlightRef.current = true;
    setIsSaving(true);

    void onSave(nextSave.tasks, nextSave.options)
      .catch(error => logger.error({ err: error }, 'Failed to persist structure editor tasks'))
      .finally(() => {
        saveInFlightRef.current = false;
        if (pendingSaveRef.current) {
          flushPersistQueue();
          return;
        }
        setIsSaving(false);
      });
  };

  const flushAdditionalInfoPersistQueue = () => {
    if (additionalInfoSaveInFlightRef.current || !onSaveAdditionalInfo) return;
    const nextSave = pendingAdditionalInfoSaveRef.current;
    if (!nextSave) {
      setIsSaving(false);
      return;
    }

    pendingAdditionalInfoSaveRef.current = null;
    additionalInfoSaveInFlightRef.current = true;
    setIsSaving(true);

    void onSaveAdditionalInfo(nextSave)
      .catch(error => logger.error({ err: error }, 'Failed to persist additional info'))
      .finally(() => {
        additionalInfoSaveInFlightRef.current = false;
        if (pendingAdditionalInfoSaveRef.current) {
          flushAdditionalInfoPersistQueue();
          return;
        }
        setIsSaving(false);
      });
  };

  const requestPersist = (nextTasks: StructureEditorTask[], options?: { rerunDescriptionOCRBoxIds?: string[] }) => {
    if (isReadOnly) return;
    const rerunDescriptionOCRBoxIds = uniqueStrings([
      ...(pendingSaveRef.current?.options?.rerunDescriptionOCRBoxIds ?? []),
      ...(options?.rerunDescriptionOCRBoxIds ?? []),
    ]);
    const previousPending = pendingSaveRef.current;
    pendingSaveRef.current = {
      tasks: nextTasks,
      options: rerunDescriptionOCRBoxIds.length > 0 ? { rerunDescriptionOCRBoxIds } : previousPending?.options,
    };
    flushPersistQueue();
  };

  const requestAdditionalInfoPersist = (nextInfo: StructureEditorAdditionalInfo[]) => {
    if (isReadOnly || !onSaveAdditionalInfo) return;
    pendingAdditionalInfoSaveRef.current = nextInfo;
    flushAdditionalInfoPersistQueue();
  };

  const actions = {
    clearSelection: () => {
      if (selectedBoxKeyRef.current && hasPendingSelectionChangesRef.current) {
        hasPendingSelectionChangesRef.current = false;
        requestPersist(tasksRef.current);
      }
      setSelectedTaskId(null);
      setSelectedAdditionalInfoId(null);
      setSelectedBoxKey(null);
      selectedBoxKeyRef.current = null;
      selectedAnchorRef.current = null;
    },
    selectBox: (taskId: string, boxKey: string) => {
      if (selectedBoxKeyRef.current && selectedBoxKeyRef.current !== boxKey && hasPendingSelectionChangesRef.current) {
        hasPendingSelectionChangesRef.current = false;
        requestPersist(tasksRef.current);
      }
      setSelectedTaskId(taskId);
      setSelectedAdditionalInfoId(null);
      setSelectedBoxKey(boxKey);
      selectedBoxKeyRef.current = boxKey;
      selectedAnchorRef.current = getSelectionAnchor(tasksRef.current, taskId, boxKey);
    },
    selectAdditionalInfoBox: (infoId: string, boxKey: string) => {
      if (selectedBoxKeyRef.current && selectedBoxKeyRef.current !== boxKey && hasPendingSelectionChangesRef.current) {
        hasPendingSelectionChangesRef.current = false;
        requestPersist(tasksRef.current);
      }
      setSelectedTaskId(null);
      setSelectedAdditionalInfoId(infoId);
      setSelectedBoxKey(boxKey);
      selectedBoxKeyRef.current = boxKey;
      selectedAnchorRef.current = null;
    },
    updateAdditionalInfoTaskAssignment: (infoId: string, taskId: string, checked: boolean) => {
      if (isReadOnly) return;
      const nextTasks = tasksRef.current.map(task => {
        if (task.id !== taskId) return task;
        const currentIds = Array.isArray(task.additionalInfoIds) ? task.additionalInfoIds : [];
        const nextIds = checked
          ? currentIds.includes(infoId)
            ? currentIds
            : [...currentIds, infoId]
          : currentIds.filter(id => id !== infoId);
        return { ...task, additionalInfoIds: nextIds };
      });
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      requestPersist(nextTasks);
    },
    updateTaskType: (taskId: string, nextTaskTypes: string[]) => {
      if (isReadOnly) return;
      const cleaned = uniqueStrings(nextTaskTypes).filter(taskType => taskType !== TaskType.DEFAULT);
      const becomesTaskFrame = cleaned.includes(TaskType.TASK_FRAME);
      const nextTasks = tasksRef.current.map(task => {
        if (task.id !== taskId) return task;
        // Flipping to a task frame means the task has no own answer: drop its own points (so no own scheme is
        // written on save, and the own field disappears immediately).
        return {
          ...task,
          taskType: cleaned,
          needsAnswer: !becomesTaskFrame,
          ownPoints: becomesTaskFrame ? undefined : task.ownPoints,
        };
      });
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      requestPersist(nextTasks);
      hasPendingSelectionChangesRef.current = false;
    },
    updateTaskMaxPoints: (taskId: string, value: string) => {
      if (isReadOnly) return;
      const trimmed = value.trim();
      const parsed = trimmed === '' ? undefined : Number(trimmed);
      if (parsed !== undefined && !Number.isFinite(parsed)) return;
      const edited = tasksRef.current.map(task =>
        task.id === taskId ? { ...task, declaredMaxPoints: parsed } : task,
      );
      // Auto-convert: if this edit made a task frame's children sum hit its declared total, drop the declared
      // so the task frame becomes dynamic. Never the just-edited task — editing a task frame total FIXES it (it
      // only goes dynamic once a child edit brings the sum to it). Does NOT touch an answerable parent's
      // own points — the total and own are independent.
      const nextTasks = reconcileMatchedTaskFrames(edited, taskId);
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      requestPersist(nextTasks);
      hasPendingSelectionChangesRef.current = false;
    },
    // An answerable parent's OWN points behave like an extra leaf child (persist into gradingScheme.maxPoints
    // on save). Own counts into the parent's sum, so — like editing a child — if own + children now hits the
    // parent's fixed total, reconcile drops the fixed total so the total goes dynamic again (no excludeId:
    // this parent may go dynamic).
    updateTaskOwnPoints: (taskId: string, value: string) => {
      if (isReadOnly) return;
      const trimmed = value.trim();
      const parsed = trimmed === '' ? undefined : Number(trimmed);
      if (parsed !== undefined && !Number.isFinite(parsed)) return;
      const edited = tasksRef.current.map(task => (task.id === taskId ? { ...task, ownPoints: parsed } : task));
      const nextTasks = reconcileMatchedTaskFrames(edited);
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      requestPersist(nextTasks);
      hasPendingSelectionChangesRef.current = false;
    },
    updateTaskPath: (taskId: string, index: number, value: string) => {
      if (isReadOnly) return;
      setTasks(prev => {
        const next = prev.map(task => {
          if (task.id !== taskId) return task;
          const currentPath = Array.isArray(task.path) ? [...task.path] : [];
          while (currentPath.length < index) currentPath.push('');
          currentPath[index] = value;
          const trimmed = [...currentPath];
          while (trimmed.length > 0 && !trimmed[trimmed.length - 1]) trimmed.pop();
          return { ...task, path: trimmed.length > 0 ? trimmed : [''] };
        });
        tasksRef.current = next;
        return next;
      });
      hasPendingSelectionChangesRef.current = true;
    },
    updateBoxText: (taskId: string, boxId: string, nextText: string) => {
      if (isReadOnly) return;
      setTasks(prev => {
        const next = prev.map(task =>
          task.id === taskId
            ? {
                ...task,
                boundingBoxes: task.boundingBoxes.map(box =>
                  box.id === boxId ? { ...box, ocrExtractedText: nextText, approved: true } : box,
                ),
                description: aggregateBoxText(
                  task.boundingBoxes.map(box =>
                    box.id === boxId ? { ...box, ocrExtractedText: nextText, approved: true } : box,
                  ),
                ),
              }
            : task,
        );
        tasksRef.current = next;
        return next;
      });
      hasPendingSelectionChangesRef.current = true;
    },
    removeBox: (taskId: string, boxId: string) => {
      if (isReadOnly) return;
      const hasDescendant = (task: StructureEditorTask) => {
        const path = Array.isArray(task.path) ? task.path : [];
        return tasksRef.current.some(other => {
          const otherPath = Array.isArray(other.path) ? other.path : [];
          return (
            other.id !== task.id &&
            otherPath.length > path.length &&
            path.every((segment, index) => otherPath[index] === segment)
          );
        });
      };
      // A task that loses its last box stays (boxless) while subtasks still hang under it; only a leaf is dropped.
      const nextTasks = tasksRef.current
        .map(task => {
          if (task.id !== taskId) return task;
          const nextBoxes = task.boundingBoxes.filter(box => box.id !== boxId);
          return { ...task, boundingBoxes: nextBoxes, description: aggregateBoxText(nextBoxes) };
        })
        .filter(task => task.id !== taskId || task.boundingBoxes.length > 0 || hasDescendant(task));

      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      requestPersist(nextTasks);

      const boxKey = `${taskId}-${boxId}`;
      if (selectedBoxKey === boxKey) {
        hasPendingSelectionChangesRef.current = false;
        setSelectedTaskId(null);
        setSelectedBoxKey(null);
        selectedBoxKeyRef.current = null;
      }
    },
    removeAdditionalInfoBox: (infoId: string, boxId: string) => {
      if (isReadOnly) return;
      const nextInfo = localAdditionalInfoRef.current
        .map(item =>
          item.id === infoId ? { ...item, boundingBoxes: item.boundingBoxes.filter(b => b.id !== boxId) } : item,
        )
        .filter(item => item.boundingBoxes.length > 0);

      localAdditionalInfoRef.current = nextInfo;
      setLocalAdditionalInfo(nextInfo);
      requestAdditionalInfoPersist(nextInfo);

      // If the whole info item was removed, clean its ID from tasks' additionalInfoIds
      const infoRemoved = !nextInfo.some(item => item.id === infoId);
      if (infoRemoved) {
        const nextTasks = tasksRef.current.map(task => ({
          ...task,
          additionalInfoIds: (task.additionalInfoIds ?? []).filter(id => id !== infoId),
        }));
        tasksRef.current = nextTasks;
        setTasks(nextTasks);
        requestPersist(nextTasks);
      }

      const boxKey = `info::${infoId}::${boxId}`;
      if (selectedBoxKey === boxKey) {
        setSelectedAdditionalInfoId(null);
        setSelectedBoxKey(null);
        selectedBoxKeyRef.current = null;
      }
    },
    handleAddTask: () => {
      if (isReadOnly) return;
      const nextTaskNumber = getNextMainTaskNumber(tasks);
      const selectedPage =
        selectedTask?.boundingBoxes?.[0]?.pageNumber ??
        tasks.find(task => task.boundingBoxes[0])?.boundingBoxes[0]?.pageNumber ??
        0;
      const newTask: StructureEditorTask = {
        id: generateUuid(),
        path: [nextTaskNumber],
        taskType: [],
        description: '',
        boundingBoxes: [
          normalizeBox({
            id: generateUuid(),
            x: 10,
            y: 10,
            width: 24,
            height: 14,
            pageNumber: selectedPage,
            ocrExtractedText: '',
          }),
        ],
      };

      const nextTasks = [...tasksRef.current, newTask];
      tasksRef.current = nextTasks;
      setTasks(nextTasks);
      const newBoxId = newTask.boundingBoxes[0]?.id ?? '';
      requestPersist(nextTasks, { rerunDescriptionOCRBoxIds: [newBoxId] });
      setSelectedTaskId(newTask.id);
      const newTaskFirstBox = newTask.boundingBoxes[0];
      setSelectedBoxKey(newTaskFirstBox ? `${newTask.id}-${newTaskFirstBox.id}` : null);
      selectedBoxKeyRef.current = newTaskFirstBox ? `${newTask.id}-${newTaskFirstBox.id}` : null;
      selectedAnchorRef.current = { path: newTask.path ?? [], boxIndex: 0 };
    },
    handleRemoveTask: () => {
      if (isReadOnly || !selectedTaskId) return;
      const selectedTask = tasksRef.current.find(task => task.id === selectedTaskId);
      if (!selectedTask) return;

      const selectedPath = Array.isArray(selectedTask.path) ? selectedTask.path : [];
      const nextTasks =
        selectedPath.length > 1
          ? tasksRef.current.filter(task => task.id !== selectedTaskId)
          : tasksRef.current.filter(task => (Array.isArray(task.path) ? task.path[0] : '') !== selectedPath[0]);

      setTasks(prev => {
        tasksRef.current = nextTasks;
        const firstRemainingTask = nextTasks[0];
        setSelectedTaskId(firstRemainingTask?.id ?? null);
        const nextFirstBox = firstRemainingTask?.boundingBoxes?.[0];
        setSelectedBoxKey(firstRemainingTask && nextFirstBox ? `${firstRemainingTask.id}-${nextFirstBox.id}` : null);
        selectedBoxKeyRef.current =
          firstRemainingTask && nextFirstBox ? `${firstRemainingTask.id}-${nextFirstBox.id}` : null;
        selectedAnchorRef.current =
          firstRemainingTask && nextFirstBox ? { path: firstRemainingTask.path ?? [], boxIndex: 0 } : null;
        return nextTasks;
      });
      requestPersist(nextTasks);
      hasPendingSelectionChangesRef.current = false;
    },
    handleAddAdditionalInfo: () => {
      if (isReadOnly) return;
      const selPage =
        selectedTask?.boundingBoxes?.[0]?.pageNumber ??
        tasks.find(task => task.boundingBoxes[0])?.boundingBoxes[0]?.pageNumber ??
        0;
      const newBoxId = generateUuid();
      const newInfo: StructureEditorAdditionalInfo = {
        id: generateUuid(),
        content: '',
        boundingBoxes: [
          normalizeBox({
            id: newBoxId,
            x: 10,
            y: 30,
            width: 24,
            height: 14,
            pageNumber: selPage,
            ocrExtractedText: '',
          }),
        ],
      };
      const nextInfo = [...localAdditionalInfoRef.current, newInfo];
      localAdditionalInfoRef.current = nextInfo;
      setLocalAdditionalInfo(nextInfo);
      requestAdditionalInfoPersist(nextInfo);

      const newBoxKey = `info::${newInfo.id}::${newBoxId}`;
      setSelectedAdditionalInfoId(newInfo.id);
      setSelectedTaskId(null);
      setSelectedBoxKey(newBoxKey);
      selectedBoxKeyRef.current = newBoxKey;
      selectedAnchorRef.current = null;
    },
    handleRemoveSelected: () => {
      if (isReadOnly) return;
      if (selectedAdditionalInfoId && selectedBoxKey) {
        const parts = selectedBoxKey.split('::');
        const boxId = parts[2];
        if (!boxId) return;
        const nextInfo = localAdditionalInfoRef.current
          .map(item =>
            item.id === selectedAdditionalInfoId
              ? { ...item, boundingBoxes: item.boundingBoxes.filter(b => b.id !== boxId) }
              : item,
          )
          .filter(item => item.boundingBoxes.length > 0);
        localAdditionalInfoRef.current = nextInfo;
        setLocalAdditionalInfo(nextInfo);
        requestAdditionalInfoPersist(nextInfo);

        // If the whole info item was removed, clean its ID from tasks' additionalInfoIds
        const removedInfoId = selectedAdditionalInfoId;
        const infoRemoved = !nextInfo.some(item => item.id === removedInfoId);
        if (infoRemoved) {
          const nextTasks = tasksRef.current.map(task => ({
            ...task,
            additionalInfoIds: (task.additionalInfoIds ?? []).filter(id => id !== removedInfoId),
          }));
          tasksRef.current = nextTasks;
          setTasks(nextTasks);
          requestPersist(nextTasks);
        }

        setSelectedAdditionalInfoId(null);
        setSelectedBoxKey(null);
        selectedBoxKeyRef.current = null;
        return;
      }
      if (!selectedTaskId) return;
      const selTask = tasksRef.current.find(task => task.id === selectedTaskId);
      if (!selTask) return;
      const selPath = Array.isArray(selTask.path) ? selTask.path : [];
      const nextTasks =
        selPath.length > 1
          ? tasksRef.current.filter(task => task.id !== selectedTaskId)
          : tasksRef.current.filter(task => (Array.isArray(task.path) ? task.path[0] : '') !== selPath[0]);
      setTasks(() => {
        tasksRef.current = nextTasks;
        const firstRemainingTask = nextTasks[0];
        const nextFirstBox = firstRemainingTask?.boundingBoxes?.[0];
        setSelectedTaskId(firstRemainingTask?.id ?? null);
        setSelectedBoxKey(firstRemainingTask && nextFirstBox ? `${firstRemainingTask.id}-${nextFirstBox.id}` : null);
        selectedBoxKeyRef.current =
          firstRemainingTask && nextFirstBox ? `${firstRemainingTask.id}-${nextFirstBox.id}` : null;
        selectedAnchorRef.current =
          firstRemainingTask && nextFirstBox ? { path: firstRemainingTask.path ?? [], boxIndex: 0 } : null;
        return nextTasks;
      });
      requestPersist(nextTasks);
      hasPendingSelectionChangesRef.current = false;
    },
    setCardHeightsPx,
    setPageAspectRatios,
    setTasks,
    setLocalAdditionalInfo,
    requestPersist,
    requestAdditionalInfoPersist,
  };

  const computed = {
    selectedTask,
    taskTypes,
    avgAspectRatio:
      Object.keys(pageAspectRatios).length > 0
        ? Object.values(pageAspectRatios).reduce((sum, r) => sum + r, 0) / Object.values(pageAspectRatios).length
        : FALLBACK_PAGE_ASPECT_RATIO,
    additionalInfoTaskMap: useMemo(() => {
      const map = new Map<string, string[][]>();
      for (const task of tasks) {
        for (const infoId of task.additionalInfoIds ?? []) {
          if (!map.has(infoId)) map.set(infoId, []);
          map.get(infoId)!.push(task.path);
        }
      }
      return map;
    }, [tasks]),
    currentPageBoxes: useMemo(() => {
      const additionalInfoMap = new Map<string, string[][]>();
      for (const task of tasks) {
        for (const infoId of task.additionalInfoIds ?? []) {
          if (!additionalInfoMap.has(infoId)) additionalInfoMap.set(infoId, []);
          additionalInfoMap.get(infoId)!.push(task.path);
        }
      }
      return [
        ...tasks.flatMap(task =>
          task.boundingBoxes.map(box => ({
            kind: 'task' as const,
            task,
            box,
            boxKey: `${task.id}-${box.id}`,
          })),
        ),
        ...localAdditionalInfo.flatMap(info =>
          info.boundingBoxes.map(box => ({
            kind: 'info' as const,
            info,
            box,
            boxKey: `info::${info.id}::${box.id}`,
            referencedTaskPaths: additionalInfoMap.get(info.id) ?? [],
          })),
        ),
      ] as PageBoxItem[];
    }, [tasks, localAdditionalInfo]),
  };

  return {
    state: {
      selectedTaskId,
      selectedBoxKey,
      selectedAdditionalInfoId,
      tasks,
      localAdditionalInfo,
      isSaving,
      cardHeightsPx,
      pageAspectRatios,
      containerSize,
      isReadOnly,
    },
    computed,
    actions,
    refs: {
      imageContainerRef,
      scrollContainerRef,
      tasksRef,
      localAdditionalInfoRef,
      cardRefsMap,
    },
    props,
  };
}

export type StructureStateStore = ReturnType<typeof useStructureState>;
